import { isEmpty } from 'es-toolkit/compat';
import { isEmptyObject, mergeWith, omitBy } from 'es-toolkit';
import { isKey, isRecord } from '@pkgs/api/utils/object';
import {
  type DeepPartial,
  PriceType,
  type RangeFilter,
  type SearchListingsFilters,
  type SearchListingsOptions,
  type SurfaceFilterKey,
} from '@pkgs/api/types';
import { type SearchFilterRequest } from '@pkgs/api/models';
import { BusinessArea, Currency, Language, type Listing, MeasurementSystem, PropertyMarketingType } from '@generated/search-bff';

export const DEFAULT_FILTERS = {
  businessArea: [BusinessArea.residential],
  propertyMarketingType: [PropertyMarketingType.sale],
} as const satisfies SearchListingsFilters;

export const DEFAULT_OPTIONS = {
  language: Language.en,
  measurementSystem: MeasurementSystem.metric,
  currency: Currency.EUR,
} as const satisfies SearchListingsOptions;

/**
 * Builds the currency suffix for BFF price filter keys from user settings currency.
 * BFF expects camelCase: first letter uppercase, rest lowercase (e.g. CZK → Czk, EUR → Eur).
 * Works for all supported currencies (EUR, CZK, USD, AED, etc.).
 */
function getCurrencySuffixForFilterKey(currency: Currency): string {
  return currency[0] + currency.slice(1).toLowerCase();
}

export function mapPriceFilter(
  filter: RangeFilter,
  propertyMarketingType: PropertyMarketingType,
  currency: Currency,
  priceType?: PriceType,
): Partial<SearchFilterRequest> {
  const priceTypePrefix = priceType ?? (propertyMarketingType === PropertyMarketingType.rent ? PriceType.RENT_ANY : PriceType.SALES_PRICE);
  const currencySuffix = getCurrencySuffixForFilterKey(currency);

  const priceDtoKey = `${priceTypePrefix}${currencySuffix}`;

  return {
    [priceDtoKey]: {
      ...filter,
    },
  };
}

export function mapSurfaceFilter(
  filter: RangeFilter,
  name: SurfaceFilterKey,
  measurementSystem: MeasurementSystem,
): Partial<SearchFilterRequest> {
  const unitSuffix = measurementSystem === MeasurementSystem.metric ? 'Sqmt' : 'Sqft';

  const surfaceDtoKey = `${name}${unitSuffix}`;

  return {
    [surfaceDtoKey]: {
      ...filter,
    },
  };
}

export function mapSearchListingsFiltersToSearchFilterRequest(
  filters: SearchListingsFilters,
  options: SearchListingsOptions,
  priceType?: PriceType,
): SearchFilterRequest {
  // remove price and/or surface filters if currency or measurement system is not provided
  const { filters: refinedFilters, options: refinedOptions } = refineFiltersAndOptions(filters, options);

  const { price, plotSurface, livingSurface, totalSurface, ...restFilters } = refinedFilters;

  // skip false values for booleans
  for (const key in restFilters) {
    if (isKey(restFilters, key) && (restFilters[key] === false || (isRecord(restFilters[key]) && isEmptyObject(restFilters[key])))) {
      delete restFilters[key];
    }
  }

  return {
    ...restFilters,
    ...(price
      ? Object.fromEntries(
          refinedFilters.propertyMarketingType.flatMap((mt: PropertyMarketingType) =>
            Object.entries(mapPriceFilter(price, mt, refinedOptions.currency, priceType)),
          ),
        )
      : {}),
    ...(plotSurface && mapSurfaceFilter(plotSurface, 'plotSurface', refinedOptions.measurementSystem)),
    ...(livingSurface && mapSurfaceFilter(livingSurface, 'livingSurface', refinedOptions.measurementSystem)),
    ...(totalSurface && mapSurfaceFilter(totalSurface, 'totalSurface', refinedOptions.measurementSystem)),
  };
}

export type SimilarListingsPriceInfo = { rangeFilter: RangeFilter; priceType: PriceType };

/**
 * Returns price info for similar properties based on the given property's price information.
 * The price range is determined by applying a 20% margin above and below the price.
 * The function also identifies the type of price (e.g., sales price, rent total, etc.) used for the calculation.
 *
 * @param property - The property object containing price information and marketing type.
 * @returns An object containing:
 *   - `rangeFilter`: A range filter object with `min` and `max` values representing the price range, or `undefined` if no valid price is found.
 *   - `priceType`: The type of price used for the calculation, or `undefined` if no valid price type is found.
 */
/** Price type keys that exist on listing price objects (BFF response). RENT_ANY is filter-only. */
const PRICE_TYPE_KEYS_ON_LISTING = ['salesPrice', 'rentTotal', 'rentNet', 'rentNetPerSquareMeter'] as const;

export function getSimilarListingsPriceInfo(property: Listing): SimilarListingsPriceInfo | undefined {
  if (!property.basePrice) return;

  const matchingField = PRICE_TYPE_KEYS_ON_LISTING.find((key) => property.basePrice![key] !== undefined);

  if (!matchingField) return;

  const priceValue = property.basePrice[matchingField];

  if (!priceValue?.min || !priceValue.max) return;

  return {
    rangeFilter: { min: priceValue.min * 0.8, max: priceValue.max * 1.2 },
    priceType: matchingField,
  };
}

/**
 * Sets the value of the key to undefined if the value is an empty object.
 * @param o object to clean
 * @returns object with undefined as value for empty objects
 */
export const cleanEmptyObjects = (o: unknown): unknown => {
  if (!isRecord(o)) return o;

  return Object.fromEntries(
    Object.entries(o).map<[string, unknown]>(([k, v]) => [k, isRecord(v) && isEmptyObject(v) ? undefined : cleanEmptyObjects(v)]),
  );
};

/**
 * Deletes empty object values from the given object.
 * @param o object to clean
 * @returns object without empty objects
 */
export const deleteEmptyObjects = (o: unknown): unknown => {
  if (!isRecord(o)) return o;

  for (const key of Object.keys(o)) {
    const value = o[key];
    if (value === undefined || (isRecord(value) && isEmptyObject(value))) {
      delete o[key];
    }
  }

  return o;
};

/**
 * Deep merge of search state with partial refinement, removing undefined values.
 */
export function mergeState<T extends Record<PropertyKey, unknown>>(state: T, refinement: DeepPartial<T>): T {
  const _refinement = cleanEmptyObjects(refinement) as DeepPartial<T>;
  const mergedObject = mergeWith<T, DeepPartial<T>>(structuredClone(state), _refinement, (targetValue, srcValue, key, target) => {
    if (Array.isArray(srcValue)) {
      return srcValue;
    }
    if (targetValue !== srcValue && srcValue === undefined && isRecord(target)) {
      const targetRecord: Record<PropertyKey, unknown> = target;
      targetRecord[key] = undefined;
    }
  });
  return omitBy<T>(mergedObject, (value) => value === undefined || value === false) as T;
}

/**
 * Refines filters and options by adding default values and removing irrelevant filters.
 * @param filters filters to refine
 * @param options options to refine
 * @returns refined filters and options
 */
export function refineFiltersAndOptions(
  filters: Partial<SearchListingsFilters>,
  options: Partial<SearchListingsOptions>,
): { filters: SearchListingsFilters; options: SearchListingsOptions } {
  const refinedFilters: SearchListingsFilters = {
    ...DEFAULT_FILTERS,
    ...filters,
  };

  const refinedOptions: SearchListingsOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  validateWithDefaults(refinedFilters, DEFAULT_FILTERS);
  validateWithDefaults(refinedOptions, DEFAULT_OPTIONS);

  return {
    filters: refinedFilters,
    options: refinedOptions,
  };
}

export function validateWithDefaults(data: SearchListingsFilters, defaults: SearchListingsFilters): void;
export function validateWithDefaults(data: SearchListingsOptions, defaults: SearchListingsOptions): void;

export function validateWithDefaults<T extends SearchListingsFilters | SearchListingsOptions>(data: T, defaults: T): void {
  for (const key of Object.keys(defaults)) {
    if (!isKey<T>(data, key)) continue;

    const defaultValue = defaults[key];
    const value = data[key];

    if (
      isEmpty(value) ||
      (Array.isArray(value) && value.some((item) => (typeof item === 'string' ? isEmpty(item.trim()) : isEmpty(item))))
    ) {
      data[key] = defaultValue;
    }
  }
}
